// Minimal, business-question-driven charts for the cockpit.
// Stripe/Linear-inspired styling: clean, no chartjunk. Uses react-apexcharts (already installed).

import { Box, Card, CardContent, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { ACCENT } from '../../brand';
import type { DashboardTimeSeries } from '../../types';
import { getLocale, t } from '../../i18n';
import type { HourlySalesChartData, PieDatum } from '../useBusinessData';
import { CHART_COLORS, UI } from '../../brand/ui';

const baseFont = "'Inter', 'Sohne', Arial, sans-serif";

// ---------------------------------------------------------------------------
// Helpers — convert the server's { date: amount } objects into sorted arrays
// ---------------------------------------------------------------------------

/** Sorts date keys chronologically and returns label+value pairs. */
function toSortedPairs(obj: Record<string, number> | undefined): Array<[string, number]> {
  if (!obj) return [];
  return Object.entries(obj)
    .filter(([k]) => k)
    .sort((a, b) => a[0].localeCompare(b[0]));
}

/** Short label for daily dates: "07-04" → "Jul 4" (locale-aware) */
function dayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}

/** Short label for monthly dates: "2026-07" → "Jul" (locale-aware) */
function monthLabel(yyyymm: string): string {
  const d = new Date(yyyymm + '-01T00:00:00');
  return d.toLocaleDateString(getLocale(), { month: 'short' });
}

// Sales trend over the last N days from fetched sales rows.
export function SalesTrendChart({ series }: { series: Array<{ x: string; y: number }> }) {
  const options: ApexOptions = {
    chart: { type: 'area', height: 200, toolbar: { show: false }, sparkline: { enabled: false }, fontFamily: baseFont },
    colors: [ACCENT[500]],
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'solid', opacity: 0.12 },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 0, right: 0 }, xaxis: { lines: { show: false } } },
    xaxis: { categories: series.map((s) => s.x), labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(val)) } },
    tooltip: { theme: 'light', y: { formatter: (val) => val.toLocaleString() } },
    legend: { show: false }
  };

  return (
    <Chart options={options} series={[{ name: t('dashboard.series.revenue'), data: series.map((s) => s.y) }]} type="area" height={200} />
  );
}

// Revenue vs Expenses — paired bar chart.
export function RevenueVsExpensesChart({ revenue, expenses, categories }: { revenue: number[]; expenses: number[]; categories: string[] }) {
  const options: ApexOptions = {
    chart: { type: 'bar', height: 200, toolbar: { show: false }, fontFamily: baseFont },
    colors: [UI.accent, UI.danger],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3 },
    xaxis: { categories, labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(val)) } },
    tooltip: { theme: 'light', y: { formatter: (val) => val.toLocaleString() } },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } }
  };

  return (
    <Chart
      options={options}
      series={[
        { name: t('dashboard.series.revenue'), data: revenue },
        { name: t('dashboard.series.expense'), data: expenses }
      ]}
      type="bar"
      height={200}
    />
  );
}

// ---------------------------------------------------------------------------
// Server-side charts (driven by ChartService via GET dashboard)
// ---------------------------------------------------------------------------

// This Week: daily Sales vs Purchases (bar chart)
export function WeeklyPerformanceChart({ data }: { data?: DashboardTimeSeries }) {
  const salePairs = toSortedPairs(data?.sale);
  const purchasePairs = toSortedPairs(data?.purchase);
  const categories = salePairs.map(([d]) => dayLabel(d));
  const hasData = categories.length > 0;

  const options: ApexOptions = {
    chart: { type: 'bar', height: 240, toolbar: { show: false }, fontFamily: baseFont },
    colors: [UI.accent, UI.navy],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: { categories, labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(Math.round(val))) } },
    tooltip: { theme: 'light', y: { formatter: (val) => val.toLocaleString(undefined, { maximumFractionDigits: 0 }) } },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } }
  };

  if (!hasData) return <EmptyChart />;

  return (
    <Chart
      options={options}
      series={[
        { name: t('dashboard.series.sale'), data: salePairs.map(([, v]) => Math.round(v)) },
        { name: t('dashboard.series.purchase'), data: purchasePairs.map(([, v]) => Math.round(v)) }
      ]}
      type="bar"
      height={240}
    />
  );
}

// This Month: daily Income vs Expenses (stacked area)
export function MonthlyCashFlowChart({ data }: { data?: DashboardTimeSeries }) {
  const incomePairs = toSortedPairs(data?.income);
  const expensePairs = toSortedPairs(data?.expense);
  const categories = incomePairs.map(([d]) => dayLabel(d));
  const hasData = categories.length > 0;

  const options: ApexOptions = {
    chart: { type: 'area', height: 240, toolbar: { show: false }, fontFamily: baseFont, stacked: false },
    colors: [UI.accent, UI.danger],
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'solid', opacity: 0.12 },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: { categories, labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(Math.round(val))) } },
    tooltip: { theme: 'light', y: { formatter: (val) => val.toLocaleString(undefined, { maximumFractionDigits: 0 }) } },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } }
  };

  if (!hasData) return <EmptyChart />;

  return (
    <Chart
      options={options}
      series={[
        { name: t('dashboard.series.income'), data: incomePairs.map(([, v]) => Math.round(v)) },
        { name: t('dashboard.series.expense'), data: expensePairs.map(([, v]) => Math.round(v)) }
      ]}
      type="area"
      height={240}
    />
  );
}

// This Year: monthly Sales (bars) + Expenses (line) — combo
export function YearlyTrendChart({ data }: { data?: DashboardTimeSeries }) {
  const salePairs = toSortedPairs(data?.sale);
  const expensePairs = toSortedPairs(data?.expense);
  const categories = salePairs.map(([m]) => monthLabel(m));
  const hasData = categories.length > 0;

  const options: ApexOptions = {
    chart: { type: 'bar', height: 240, toolbar: { show: false }, fontFamily: baseFont },
    colors: [UI.accent, UI.danger],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    stroke: { curve: 'smooth', width: [0, 3] },
    fill: { opacity: [1, 1] },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: { categories, labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(Math.round(val))) } },
    tooltip: { theme: 'light', y: { formatter: (val) => val.toLocaleString(undefined, { maximumFractionDigits: 0 }) } },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } },
    markers: { size: 0, hover: { size: 4 } }
  };

  if (!hasData) return <EmptyChart />;

  return (
    <Chart
      options={options}
      series={[
        { name: t('dashboard.series.sale'), type: 'bar', data: salePairs.map(([, v]) => Math.round(v)) },
        { name: t('dashboard.series.expense'), type: 'line', data: expensePairs.map(([, v]) => Math.round(v)) }
      ]}
      type="bar"
      height={240}
    />
  );
}

export function DashboardPieChart({ data }: { data: PieDatum[] }) {
  if (!data.length) return <EmptyChart />;

  const labels = data.map((row) => row.label);
  const options: ApexOptions = {
    chart: { type: 'pie', height: 260, toolbar: { show: false }, fontFamily: baseFont },
    labels,
    colors: CHART_COLORS,
    dataLabels: {
      enabled: true,
      formatter: (value) => `${Math.round(Number(value))}%`,
      style: { fontSize: '11px', fontWeight: 700 }
    },
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '12px',
      markers: { size: 6, shape: 'circle' as const }
    },
    stroke: { width: 2, colors: [UI.white] },
    tooltip: {
      theme: 'light',
      y: { formatter: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }) }
    },
    plotOptions: {
      pie: {
        expandOnClick: false
      }
    }
  };

  return <Chart options={options} series={data.map((row) => Math.round(row.value))} type="pie" height={260} />;
}

export function SalesByHourChart({ data }: { data: HourlySalesChartData }) {
  const hasData = data.series.some((series) => series.data.some((value) => value > 0));
  if (!hasData) return <EmptyChart />;

  const options: ApexOptions = {
    chart: { type: 'bar', height: 300, toolbar: { show: false }, fontFamily: baseFont },
    colors: CHART_COLORS.slice(0, 5),
    plotOptions: { bar: { borderRadius: 3, columnWidth: '72%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: {
      categories: data.categories,
      tickAmount: 12,
      labels: { rotate: -45, style: { fontSize: '10px', colors: UI.textSecondary } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: UI.textSecondary },
        formatter: (value) => (value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(Math.round(value)))
      }
    },
    tooltip: {
      theme: 'light',
      y: { formatter: (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }) }
    },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } }
  };

  return <Chart options={options} series={data.series} type="bar" height={300} />;
}

function EmptyChart() {
  return (
    <Box sx={{ height: 240, display: 'grid', placeItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {t('common.notEnoughData')}
      </Typography>
    </Box>
  );
}

export function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card sx={{ overflow: 'hidden' }}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: UI.ink }}>
          {title}
        </Typography>
        <Box>{children}</Box>
      </CardContent>
    </Card>
  );
}
